import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { Plus, ArrowRight, Warning, Truck, ShoppingCart } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import BestelLeverancierModal from '@/components/BestelLeverancierModal';
import WhatsNewBanner from '@/components/WhatsNewBanner';
import OnboardingFeaturesWizard from '@/components/OnboardingFeaturesWizard';
import MijnDagWidget from '@/components/MijnDagWidget';
import PeppolComplianceWidget from '@/components/PeppolComplianceWidget';
import VandaagBord from '@/components/VandaagBord';
import KlantbezoekenWidget from '@/components/KlantbezoekenWidget';
import InstallAppButton from '@/components/InstallAppButton';
import { mergeFeatures } from '@/lib/features';
import { useCompanyFunctions } from '@/contexts/CompanyFunctionsContext';

const BADGE_MAP = {
  betaald: 'badge-green', goedgekeurd: 'badge-green', geleverd: 'badge-green',
  besteld: 'badge-green', gekoppeld: 'badge-green', actief: 'badge-green',
  openstaand: 'badge-yellow', ongekoppeld: 'badge-yellow', laag: 'badge-yellow',
  verzonden: 'badge-blue', concept: 'badge-gray',
  'productie in afwachting': 'badge-purple',
  'nog te leveren': 'badge-orange',
  vervallen: 'badge-red', afgewezen: 'badge-red', kritiek: 'badge-red',
};
const TYPE_LABELS = { offerte: 'Offerte', bestelbon: 'Bestelbon', leverbon: 'Leverbon', proforma: 'Proforma', factuur: 'Factuur' };
const TYPE_PILL = { offerte: 'badge-purple', bestelbon: 'badge-blue', leverbon: 'badge-green', proforma: 'badge-yellow', factuur: 'badge-green' };
const MAANDEN = ['Jan','Feb','Mrt','Apr','Mei','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];
const PIE_COLORS = ['#002FA7', '#059669', '#D97706', '#7C3AED', '#DC2626', '#2563EB'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
};

export default function DashboardPage() {
  const { isVisible } = useCompanyFunctions();
  const [data, setData] = useState(null);
  const [rapport, setRapport] = useState(null);
  const [klanten, setKlanten] = useState([]);
  const [voorraadLaag, setVoorraadLaag] = useState(null); // { totaal, groepen }
  const [bestelGroep, setBestelGroep] = useState(null);
  const [comm, setComm] = useState(null);
  const [topVerkopers, setTopVerkopers] = useState(null);
  const [topVarianten, setTopVarianten] = useState(null);
  const [antidumping, setAntidumping] = useState(null);
  const [kostenAgenda, setKostenAgenda] = useState(null);
  const [features, setFeatures] = useState(() => mergeFeatures({}));
  const [miniTourKeys, setMiniTourKeys] = useState(null);
  const navigate = useNavigate();

  const loadLaag = () => API.get('/voorraad/laag').then(r => setVoorraadLaag(r.data)).catch(() => {});
  useEffect(() => {
    API.get('/dashboard').then(r => setData(r.data)).catch(() => {});
    API.get('/dashboard/communicatie').then(r => setComm(r.data)).catch(() => {});
    API.get('/dashboard/top-verkopers').then(r => setTopVerkopers(r.data)).catch(() => {});
    API.get('/dashboard/top-varianten?limit=10&dagen=90').then(r => setTopVarianten(r.data)).catch(() => {});
    API.get('/dashboard/antidumping').then(r => setAntidumping(r.data)).catch(() => {});
    API.get('/kosten/agenda').then(r => setKostenAgenda(r.data)).catch(() => setKostenAgenda(null));
    API.get('/rapportage').then(r => setRapport(r.data)).catch(() => {});
    API.get('/klanten').then(r => setKlanten(r.data)).catch(() => {});
    API.get('/instellingen').then(r => setFeatures(mergeFeatures(r.data?.features || {}))).catch(() => {});
    loadLaag();
  }, []);

  const kNaam = (kid) => {
    const k = klanten.find(x => x.id === kid);
    return k ? (k.bedrijf || `${k.voorn} ${k.achtern}`) : kid || '-';
  };

  if (!data) return <div className="text-gray-400">Laden...</div>;

  // Chart data
  const omzetRaw = rapport?.omzet_maand || {};
  const curYear = new Date().getFullYear();
  const omzetData = MAANDEN.map((naam, i) => {
    const key = `${curYear}-${String(i + 1).padStart(2, '0')}`;
    return { naam, omzet: omzetRaw[key] || 0 };
  });

  const docCounts = rapport?.doc_counts || {};
  const pieData = Object.entries(docCounts).filter(([,v]) => v > 0).map(([k, v]) => ({
    name: TYPE_LABELS[k] || k, value: v,
  }));

  // Running total for area chart
  let running = 0;
  const trendData = omzetData.map(d => {
    running += d.omzet;
    return { naam: d.naam, cumulatief: running };
  });

  return (
    <div data-testid="dashboard-page">
      <WhatsNewBanner onStartTour={(keys) => setMiniTourKeys(keys)} />
      <VandaagBord />
      <div className="mb-6">
        <h1 className="page-heading mb-3">Dashboard</h1>
        <div className="flex flex-wrap gap-2">
          {isVisible('/facturen') && <button className="btn btn-primary inline-flex items-center justify-center gap-1.5 font-bold" onClick={() => navigate('/documenten/nieuw?type=factuur')} data-testid="quick-factuur-btn">
            <Plus size={14} weight="bold" /> Factuur
          </button>}
          {isVisible('/leveringsbonnen') && features.doc_leverbon?.actief !== false && <button className="btn btn-secondary inline-flex items-center justify-center gap-1.5 font-bold" onClick={() => navigate('/documenten/nieuw?type=leverbon')} data-testid="quick-leverbon-btn">
            <Plus size={14} weight="bold" /> Leverbon
          </button>}
          {isVisible('/verkoop') && <button
            className="btn inline-flex items-center justify-center gap-1.5 font-bold text-white"
            style={{ background: '#FF6B0D', borderColor: '#FF6B0D', boxShadow: '0 0 10px rgba(255,107,13,0.25)' }}
            onClick={() => navigate('/verkoop')}
            data-testid="quick-verkoop-onderweg-btn"
            title="Mobiele bestelbon-invoer voor verkopers onderweg"
          >
            <ShoppingCart size={14} weight="bold" /> Verkoop
          </button>}
        </div>
      </div>

      {/* Mijn dag — actiegerichte taken voor vandaag */}
      <MijnDagWidget />

      {/* Klantbezoeken — overdue klanten dispatch widget */}
      {isVisible('/klantbezoeken') && <KlantbezoekenWidget />}

      {/* PWA Install-banner (verschijnt enkel op mobile + niet-geïnstalleerd) */}
      <InstallAppButton variant="banner" />

      {/* Peppol-compliance widget — zichtbaar enkel als er Peppol-klanten zijn */}
      <div className="mb-6">
        <PeppolComplianceWidget />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="kpi-card" data-testid="kpi-betaald">
          <div className="kpi-label">Omzet betaald</div>
          <div className="kpi-value text-[#008A27]">{fmt(data.betaald)}</div>
        </div>
        <div className="kpi-card" data-testid="kpi-openstaand">
          <div className="kpi-label">Openstaand</div>
          <div className="kpi-value text-[#E5B300]">{fmt(data.openstaand)}</div>
        </div>
        <div className="kpi-card" data-testid="kpi-voorraad">
          <div className="kpi-label">Voorraadwaarde</div>
          <div className="kpi-value text-[#002FA7]">{fmt(data.voorraad_waarde)}</div>
          {isVisible('/voorraad') && data.negatieve_voorraad_count > 0 && (
            <a
              href="/inventaris"
              className="mt-1 inline-block text-[11px] text-amber-700 hover:underline"
              data-testid="kpi-voorraad-negatief-warn"
            >
              ⚠ {data.negatieve_voorraad_count} artikel(s) met negatieve voorraad
            </a>
          )}
        </div>
        <div className="kpi-card" data-testid="kpi-klanten">
          <div className="kpi-label">Klanten</div>
          <div className="kpi-value">{data.klanten}</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Bar: Omzet per maand */}
        <div className="section-card lg:col-span-2">
          <div className="section-header"><span className="section-title">Omzet per maand</span></div>
          <div className="p-4" style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={1} initialDimension={{width:320,height:208}}>
              <BarChart data={omzetData} barSize={20}>
                <XAxis dataKey="naam" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="omzet" name="Omzet" fill="#002FA7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie: Documentverdeling */}
        <div className="section-card">
          <div className="section-header"><span className="section-title">Documenten</span></div>
          <div className="p-4 flex flex-col items-center" style={{ height: 240 }}>
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160} minWidth={1} initialDimension={{width:240,height:160}}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={3} dataKey="value">
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, name) => [`${v} stuks`, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
                  {pieData.map((d, i) => (
                    <span key={d.name} className="flex items-center gap-1 text-[10px] text-gray-500">
                      <span className="w-2 h-2 inline-block" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      {d.name} ({d.value})
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-gray-300 text-sm py-12">Geen data</div>
            )}
          </div>
        </div>
      </div>

      {/* Trend + Recente docs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Area: Cumulatieve omzet */}
        <div className="section-card">
          <div className="section-header"><span className="section-title">Cumulatieve omzet</span></div>
          <div className="p-4" style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={1} initialDimension={{width:320,height:168}}>
              <AreaChart data={trendData}>
                <defs><linearGradient id="gradOmzet" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#002FA7" stopOpacity={0.15} /><stop offset="95%" stopColor="#002FA7" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="naam" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="cumulatief" name="Cumulatief" stroke="#002FA7" strokeWidth={2} fill="url(#gradOmzet)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recente documenten tabel */}
        <div className="section-card lg:col-span-2">
          <div className="section-header">
            <span className="section-title">Recente documenten</span>
            {isVisible('/facturen') && <button className="btn btn-ghost btn-sm" onClick={() => navigate('/facturen')} data-testid="view-all-docs">
              Alle <ArrowRight size={14} />
            </button>}
          </div>
          <div className="section-body">
            <table className="data-table">
              <thead><tr><th>Nummer</th><th>Type</th><th>Klant</th><th className="right">Totaal</th><th>Status</th></tr></thead>
              <tbody>
                {(data.recente_docs || []).slice(0, 8).map(d => (
                  <tr
                    key={d.id}
                    className="cursor-pointer hover:bg-[#F0F4FF] transition-colors"
                    onClick={() => navigate(`/documenten/${d.id}/bewerk`)}
                    data-testid={`dashboard-doc-row-${d.id}`}
                  >
                    <td className="mono"><span className="doc-nr">{d.id}</span></td>
                    <td><span className={`type-pill ${TYPE_PILL[d.type] || 'badge-gray'}`}>{TYPE_LABELS[d.type] || d.type}</span></td>
                    <td>{kNaam(d.klant_id)}</td>
                    <td className="right">{fmt(d.totaal)}</td>
                    <td><span className={`badge ${BADGE_MAP[d.status] || 'badge-gray'}`}>{d.status}</span></td>
                  </tr>
                ))}
                {(!data.recente_docs || data.recente_docs.length === 0) && (
                  <tr><td colSpan="5" className="text-center text-gray-400 py-6">Geen documenten</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Top verkopers widget */}
      {isVisible('/bestelbonnen') && topVerkopers && topVerkopers.verkopers && topVerkopers.verkopers.length > 0 && (
        <div className="section-card mb-4" data-testid="top-verkopers-widget">
          <div className="section-header flex items-center justify-between">
            <span className="section-title inline-flex items-center gap-2">
              🏆 Top verkopers
              <span className="text-[10px] font-normal text-gray-500 normal-case tracking-normal">— {topVerkopers.maand_label} · {topVerkopers.aantal_bestelbonnen} bestelbonnen · {fmt(topVerkopers.totaal_maand)}</span>
            </span>
            {topVerkopers.trend_pct !== null && topVerkopers.totaal_vorige_maand > 0 && (
              <span className={`text-[11px] font-bold px-2 py-0.5 ${topVerkopers.trend_pct >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {topVerkopers.trend_pct >= 0 ? '↑' : '↓'} {Math.abs(topVerkopers.trend_pct)}% vs vorige maand
              </span>
            )}
          </div>
          <div className="section-body p-0">
            <div className="divide-y divide-gray-100">
              {topVerkopers.verkopers.slice(0, 5).map((v, i) => {
                const maxTotaal = topVerkopers.verkopers[0]?.totaal || 1;
                const pct = Math.round((v.totaal / maxTotaal) * 100);
                const rankCol = i === 0 ? 'bg-amber-400 text-amber-900' : i === 1 ? 'bg-gray-300 text-gray-800' : i === 2 ? 'bg-orange-300 text-orange-900' : 'bg-gray-100 text-gray-600';
                return (
                  <div key={v.verkoper_id} className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/documenten/bestelbon?verkoper=${v.verkoper_id}`)} data-testid={`top-verkoper-${v.verkoper_id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 flex items-center justify-center font-bold text-sm ${rankCol}`}>#{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-sm truncate">{v.naam}</span>
                          <span className="font-mono font-bold text-sm text-[#002FA7] whitespace-nowrap">{fmt(v.totaal)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-2 bg-gray-100 overflow-hidden">
                            <div className="h-full bg-[#002FA7]" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] text-gray-500 whitespace-nowrap">{v.aantal} bestelbonnen</span>
                          {v.delta_pct !== null && v.delta_pct !== undefined && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 ${v.delta_pct >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                              {v.delta_pct >= 0 ? '↑' : '↓'} {Math.abs(v.delta_pct)}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-500 italic">
              Klik op een verkoper om zijn bestelbonnen te filteren. Verkopers beheren via <a href="/instellingen?tab=verkopers" className="text-[#002FA7] hover:underline font-bold">Instellingen → Verkopers</a>.
            </div>
          </div>
        </div>
      )}

      {/* Kosten-agenda — terugkerende kosten volgende 60 dagen (admin only) */}
      {isVisible('/kosten-rapport') && kostenAgenda && kostenAgenda.items && kostenAgenda.items.length > 0 && (() => {
        // Groepeer per datum
        const byDate = {};
        kostenAgenda.items.forEach(it => {
          if (!byDate[it.datum]) byDate[it.datum] = [];
          byDate[it.datum].push(it);
        });
        const dates = Object.keys(byDate).sort();
        const fmtDatum = (iso) => {
          const d = new Date(iso);
          const MAAND_KORT = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'];
          const DAG_KORT = ['zo','ma','di','wo','do','vr','za'];
          return `${DAG_KORT[d.getDay()]} ${d.getDate()} ${MAAND_KORT[d.getMonth()]}`;
        };
        const urgencyBadge = (dagen) => {
          if (dagen <= 3) return { cls: 'bg-red-100 text-red-700 border border-red-200', label: dagen === 0 ? 'vandaag' : dagen === 1 ? 'morgen' : `over ${dagen}d` };
          if (dagen <= 7) return { cls: 'bg-amber-100 text-amber-800 border border-amber-200', label: `over ${dagen}d` };
          if (dagen <= 30) return { cls: 'bg-blue-50 text-blue-700 border border-blue-200', label: `over ${dagen}d` };
          return { cls: 'bg-gray-50 text-gray-600 border border-gray-200', label: `over ${dagen}d` };
        };
        const INTERVAL_LABEL = { maandelijks: 'maand', kwartaal: 'kwartaal', jaarlijks: 'jaar', wekelijks: 'week' };
        return (
          <div className="section-card mb-4" data-testid="kosten-agenda-widget">
            <div className="section-header flex items-center justify-between">
              <span className="section-title inline-flex items-center gap-2">
                📅 Kosten-agenda
                <span className="text-[10px] font-normal text-gray-500 normal-case tracking-normal">
                  — volgende 60 dagen · {kostenAgenda.items.length} gepland · <strong className="text-gray-800">{fmt(kostenAgenda.totaal_verwacht)}</strong> verwacht
                </span>
              </span>
              <button
                type="button"
                className="text-[11px] text-[#002FA7] font-bold hover:underline"
                onClick={() => navigate('/kosten-rapport')}
                data-testid="kosten-agenda-naar-rapport"
              >
                → Kostenrapport
              </button>
            </div>
            <div className="section-body p-0 divide-y divide-gray-100">
              {dates.slice(0, 8).map(datum => (
                <div key={datum} className="px-4 py-2.5" data-testid={`kosten-agenda-datum-${datum}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-900 capitalize">{fmtDatum(datum)}</span>
                      {(() => {
                        const dagen = byDate[datum][0].dagen_tot;
                        const b = urgencyBadge(dagen);
                        return <span className={`text-[10px] font-bold px-1.5 py-0.5 ${b.cls}`}>{b.label}</span>;
                      })()}
                    </div>
                    <span className="text-xs font-mono font-bold text-gray-700">
                      {fmt(byDate[datum].reduce((s, i) => s + (i.bedrag_excl || 0), 0))}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {byDate[datum].map((it, idx) => (
                      <div
                        key={`${it.sjabloon_id}-${idx}`}
                        className="flex items-center gap-2 text-[11px] pl-2 hover:bg-gray-50 cursor-pointer py-0.5"
                        onClick={() => navigate('/kosten-rapport')}
                        data-testid={`kosten-agenda-item-${it.sjabloon_id}`}
                      >
                        <span className="font-mono text-gray-400 w-10 shrink-0">{it.uur}</span>
                        <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 font-bold uppercase tracking-wide text-[9px] shrink-0">{it.categorie || '—'}</span>
                        <span className="flex-1 min-w-0 truncate text-gray-800">
                          {it.omschrijving}
                          {it.leverancier && <span className="text-gray-400"> · {it.leverancier}</span>}
                        </span>
                        <span className="text-[9px] text-gray-400 italic shrink-0">per {INTERVAL_LABEL[it.interval] || it.interval}</span>
                        <span className="font-mono font-bold text-gray-900 shrink-0 text-right" style={{ minWidth: 70 }}>{fmt(it.bedrag_excl)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {dates.length > 8 && (
                <div className="px-4 py-2 bg-gray-50 text-[11px] text-gray-500 italic text-center">
                  + {dates.length - 8} dag{dates.length - 8 > 1 ? 'en' : ''} meer — bekijk volledige agenda in Kostenrapport
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Top bestverkochte varianten — alleen tonen wanneer module actief en data beschikbaar */}
      {isVisible('/artikels') && topVarianten && topVarianten.items?.length > 0 && (
        <div className="section-card mb-4" data-testid="top-varianten-widget">
          <div className="section-header flex items-center justify-between">
            <span className="section-title inline-flex items-center gap-2">🎨 Bestverkochte varianten <span className="text-[10px] font-normal text-gray-500 normal-case tracking-normal">— laatste {topVarianten.dagen} dagen</span></span>
            <button type="button" className="text-[11px] text-blue-700 hover:underline" onClick={() => navigate('/artikels')} data-testid="top-varianten-naar-artikels">→ Beheer varianten</button>
          </div>
          <div className="section-body p-0 divide-y divide-gray-100">
            {topVarianten.items.slice(0, 5).map(it => {
              const fotoSrc = it.foto_url ? (it.foto_url.startsWith('http') ? it.foto_url : `${process.env.REACT_APP_BACKEND_URL}${it.foto_url}`) : null;
              const variantLabel = Object.values(it.combo || {}).join(' / ');
              return (
                <div key={`${it.art_id}-${it.combo_key}`} className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/artikels?edit=${it.art_id}`)} data-testid={`top-variant-${it.combo_key}`}>
                  <div className="flex items-center gap-3">
                    {fotoSrc ? (
                      <img src={fotoSrc} alt="" className="w-11 h-11 object-cover rounded border border-gray-200 flex-shrink-0" />
                    ) : (
                      <div className="w-11 h-11 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-lg flex-shrink-0">📦</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm truncate">{it.omschrijving}</span>
                        <span className="font-bold text-sm whitespace-nowrap text-emerald-700">{it.verkocht}× verkocht</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-mono font-bold rounded">{variantLabel}</span>
                        <span className="text-[10px] text-gray-500">{it.variant_code} · voorraad <b className={it.voorraad <= 0 ? 'text-red-600' : (it.voorraad < 5 ? 'text-amber-600' : 'text-emerald-600')}>{it.voorraad}</b></span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Antidumping risico widget — toont enkel als er artikels onderhevig zijn */}
      {isVisible('/artikels') && antidumping && antidumping.totaal_artikels > 0 && (
        <div className="section-card mb-4 border-2 border-rose-200" data-testid="antidumping-widget">
          <div className="section-header flex items-center justify-between" style={{ background: 'linear-gradient(90deg, #fff1f2 0%, #ffe4e6 100%)' }}>
            <span className="section-title inline-flex items-center gap-2 text-rose-900">
              ⚠ Antidumping-risico
              <span className="text-[10px] font-normal text-rose-700 normal-case tracking-normal">
                — {antidumping.totaal_artikels} artikel{antidumping.totaal_artikels > 1 ? 's' : ''} onderhevig · {fmt(antidumping.totaal_voorraad_waarde)} voorraadwaarde
              </span>
            </span>
            <button
              type="button"
              className="text-[11px] text-rose-700 font-bold hover:underline"
              onClick={() => navigate('/artikels?tab=taric')}
              data-testid="antidumping-naar-taric"
            >
              → Bekijk in TARIC →
            </button>
          </div>
          <div className="section-body p-0">
            {/* Per land samenvatting */}
            {antidumping.per_land.length > 0 && (
              <div className="px-4 py-3 bg-rose-50/50 border-b border-rose-100 flex flex-wrap gap-2">
                {antidumping.per_land.map(l => (
                  <div key={l.land} className="bg-white border border-rose-200 rounded-lg px-3 py-2 text-xs" data-testid={`antidumping-land-${l.land}`}>
                    <div className="font-mono font-bold text-rose-900">{l.land}</div>
                    <div className="text-gray-700">{l.aantal} artikel{l.aantal > 1 ? 's' : ''}</div>
                    <div className="font-bold text-rose-700">{fmt(l.voorraad_waarde)}</div>
                  </div>
                ))}
              </div>
            )}
            {/* Top items */}
            <div className="divide-y divide-rose-50">
              {antidumping.top_items.slice(0, 5).map(it => (
                <div key={it.id} className="px-4 py-2.5 hover:bg-rose-50/40 cursor-pointer" onClick={() => navigate(`/artikels?edit=${it.id}`)} data-testid={`antidumping-item-${it.art_code}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-rose-100 border border-rose-200 rounded flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-mono font-bold text-rose-800">{it.oorsprong}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm truncate">{it.omschrijving}</span>
                        <span className="font-mono font-bold text-sm text-rose-700 whitespace-nowrap">{fmt(it.voorraad_waarde)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] font-mono text-gray-500">{it.art_code} · HS {it.hs_code || '—'} · voorraad {it.voorraad}</span>
                      </div>
                      <div className="text-[11px] text-rose-700 italic leading-tight mt-0.5 truncate">{it.antidumping}</div>
                    </div>
                  </div>
                </div>
              ))}
              {antidumping.top_items.length > 5 && (
                <div className="px-4 py-2 bg-rose-50/30 text-[11px] text-rose-700 italic">
                  + {antidumping.top_items.length - 5} ander{antidumping.top_items.length - 5 > 1 ? 'e' : ''} artikel{antidumping.top_items.length - 5 > 1 ? 's' : ''} — bekijk volledige lijst in Import & TARIC
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Communicatie-kanalen widget */}
      {comm && comm.totaal_maand > 0 && (
        <div className="section-card mb-4" data-testid="communicatie-widget">
          <div className="section-header flex items-center justify-between">
            <span className="section-title inline-flex items-center gap-2">
              📬 Communicatie per kanaal
              <span className="text-[10px] font-normal text-gray-500 normal-case tracking-normal">— {comm.maand_label}</span>
            </span>
            {comm.totaal_vorige_maand > 0 && (
              <span className={`text-[11px] font-bold px-2 py-0.5 ${comm.trend_pct >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {comm.trend_pct >= 0 ? '↑' : '↓'} {Math.abs(comm.trend_pct)}% vs vorige maand
              </span>
            )}
          </div>
          <div className="section-body p-0">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Donut chart */}
              <div className="p-4 flex items-center justify-center border-r border-gray-100">
                <ResponsiveContainer width="100%" height={220} minWidth={1} initialDimension={{width:320,height:220}}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'E-mail', value: comm.kanalen.email.aantal, color: '#002FA7' },
                        { name: 'SMS', value: comm.kanalen.sms.aantal, color: '#7C3AED' },
                        { name: 'WhatsApp', value: comm.kanalen.whatsapp.aantal, color: '#25D366' },
                      ].filter(d => d.value > 0)}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {[0, 1, 2].map((_, i) => (
                        <Cell key={i} fill={['#002FA7', '#7C3AED', '#25D366'][i]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v} berichten`, '']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute text-center pointer-events-none" style={{ marginLeft: '-20px' }}>
                  <div className="text-3xl font-bold text-gray-900">{comm.totaal_maand}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">totaal</div>
                </div>
              </div>
              {/* Breakdown per kanaal */}
              <div className="p-4 space-y-3">
                {[
                  { key: 'email', label: 'E-mail', icon: '✉️', color: '#002FA7', bg: 'bg-blue-50', text: 'text-blue-700' },
                  { key: 'sms', label: 'SMS', icon: '📱', color: '#7C3AED', bg: 'bg-purple-50', text: 'text-purple-700' },
                  { key: 'whatsapp', label: 'WhatsApp', icon: '💬', color: '#25D366', bg: 'bg-green-50', text: 'text-green-700' },
                ].map(k => {
                  const d = comm.kanalen[k.key];
                  const delta = d.aantal - d.vorige;
                  return (
                    <div key={k.key} className="flex items-center gap-3" data-testid={`comm-${k.key}`}>
                      <div className={`w-10 h-10 ${k.bg} flex items-center justify-center text-xl shrink-0`}>{k.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="text-sm font-bold text-gray-900">{k.label}</span>
                          <span className="text-xs font-mono">
                            <strong className={k.text}>{d.aantal}</strong>
                            <span className="text-gray-400"> ({d.pct}%)</span>
                            {d.vorige > 0 && (
                              <span className={`ml-1.5 text-[10px] ${delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {delta >= 0 ? '+' : ''}{delta}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-100 overflow-hidden">
                          <div className="h-full transition-all" style={{ width: `${d.pct}%`, background: k.color }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {/* Inzicht tip */}
                {(() => {
                  const top = ['email', 'sms', 'whatsapp'].reduce((a, b) => comm.kanalen[a].aantal >= comm.kanalen[b].aantal ? a : b);
                  const labels = { email: 'e-mail', sms: 'SMS', whatsapp: 'WhatsApp' };
                  return (
                    <div className="mt-2 pt-3 border-t border-gray-100 text-[10px] text-gray-500 italic">
                      💡 Uw klanten gebruiken vooral <strong className="not-italic text-gray-700">{labels[top]}</strong> — focus uw strategie hierop.
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isVisible('/voorraad') && <div className="section-card">
          <div className="section-header">
            <span className="section-title inline-flex items-center gap-2">
              {voorraadLaag && voorraadLaag.totaal > 0 && <Warning size={16} weight="fill" className="text-amber-600" />}
              Kritieke stock {voorraadLaag && voorraadLaag.totaal > 0 && <span className="text-xs text-amber-700 font-semibold ml-1">({voorraadLaag.totaal})</span>}
            </span>
          </div>
          <div className="section-body" data-testid="kritieke-stock-widget">
            {!voorraadLaag || voorraadLaag.totaal === 0 ? (
              <div className="text-center text-gray-400 py-6 text-sm">Alles op voorraad — geen bestellingen nodig</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {voorraadLaag.groepen.map(g => {
                  const isOnbekend = g.leverancier === 'Onbekend';
                  return (
                  <div key={g.leverancier} className={`py-3 px-3 hover:bg-gray-50 ${isOnbekend ? 'bg-amber-50/50' : ''}`} data-testid={`stock-groep-${g.leverancier}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs">
                        <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                          {isOnbekend && <Warning size={12} weight="fill" className="text-amber-600" />}
                          {g.leverancier}
                        </div>
                        <div className="text-gray-500">
                          {g.artikelen.length} artikel(en) onder minimumvoorraad
                          {isOnbekend && <span className="ml-1 text-amber-700 font-medium"> — leverancier nog te kiezen</span>}
                        </div>
                      </div>
                      {isVisible('/aankoopbestellingen') && <button
                        className={`btn btn-sm inline-flex items-center gap-1.5 whitespace-nowrap ${isOnbekend ? 'btn-secondary' : 'btn-primary'}`}
                        onClick={() => setBestelGroep(g)}
                        data-testid={`bestel-btn-${g.leverancier}`}
                        title={isOnbekend ? 'Kies eerst een leverancier voor deze bestelling' : `Bestelling aanmaken bij ${g.leverancier}`}
                      >
                        <Truck size={13} /> {isOnbekend ? 'Kies leverancier…' : `Bestel bij ${g.leverancier.length > 22 ? g.leverancier.slice(0, 20) + '…' : g.leverancier}`}
                      </button>}
                    </div>
                    <table className="w-full text-[11px]">
                      <tbody>
                        {g.artikelen.slice(0, 5).map(a => (
                          <tr key={a.id} className="text-gray-600">
                            <td className="py-0.5 font-mono text-[10px] text-[#002FA7]" style={{ width: 100 }}>{a.art_code || '-'}</td>
                            <td className="py-0.5 truncate">{a.omschrijving}</td>
                            <td className="py-0.5 text-right font-bold text-red-600" style={{ width: 60 }}>{a.voorraad}</td>
                            <td className="py-0.5 text-right text-gray-400" style={{ width: 40 }}>/ {a.min_voorraad}</td>
                          </tr>
                        ))}
                        {g.artikelen.length > 5 && (
                          <tr><td colSpan="4" className="py-0.5 text-center text-gray-400 text-[10px]">+ {g.artikelen.length - 5} meer…</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>}
        {isVisible('/bank') && <div className="section-card">
          <div className="section-header">
            <span className="section-title">Ongekoppelde betalingen</span>
            {data.bank_ongekoppeld > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/bank')} data-testid="go-bank-btn">Koppelen</button>
            )}
          </div>
          <div className="section-body p-5">
            {data.bank_ongekoppeld > 0 ? (
              <div className="alert-warn">{data.bank_ongekoppeld} verrichting(en) wachten op koppeling</div>
            ) : (
              <div className="text-center text-gray-400 py-4">Alles gekoppeld</div>
            )}
          </div>
        </div>}
      </div>

      <BestelLeverancierModal
        open={!!bestelGroep}
        groep={bestelGroep}
        bekendeLeveranciers={voorraadLaag?.bekende_leveranciers || []}
        onClose={() => setBestelGroep(null)}
        onSaved={() => loadLaag()}
      />

      {miniTourKeys && (
        <OnboardingFeaturesWizard
          filterKeys={miniTourKeys}
          onClose={() => setMiniTourKeys(null)}
          onComplete={() => setMiniTourKeys(null)}
        />
      )}
    </div>
  );
}
